<template>
  <v-container class="pa-0">
    <v-card>
      <v-toolbar dense flat dark :color="toolbarColor" rounded>
        <v-toolbar-title :class="headingClass">
          {{ cardTitle }}
        </v-toolbar-title>
      </v-toolbar>

      <v-card :loading="isLoading">
        <template v-slot:progress>
          <v-progress-linear
            indeterminate
            :color="appColor_Progress"
          ></v-progress-linear>
        </template>

        <!-- Chart -->
        <v-list-item two-line>
          <v-list-item-content>
            <v-list-item-subtitle v-html="chartTitle">
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-card-text>
          <template v-if="noChartDataForTimePeriod">
            <span v-html="noDataStatement"></span>
          </template>
          <template v-else>
            <bar-chart :chart-data="chart" :options="chartOptions" />
          </template>
        </v-card-text>
      </v-card>
    </v-card>
  </v-container>
</template>
<script>
import appService from '@/app.service.js'
import { mapGetters } from 'vuex'
import { AppColor, DashboardClass, LanguageId } from '@/config.js'
import { BarChart } from '@/charts/barChart.js'

export default {
  name: 'DashboardBarChart',
  components: {
    BarChart
  },
  props: {
    languageId: {
      type: Number,
      default: LanguageId.English
    },
    dataSource: {
      type: String,
      default: null
    },
    selectedTimePeriodValue: {
      type: String,
      default: 'WeekToDate'
    },
    cardTitle: {
      type: String,
      default: null
    },
    trigger: {
      type: Boolean,
      default: false
    },
    backgroundColor: {
      type: String,
      default: 'green'
    },
    tickStepSize: {
      type: Number,
      default: 1
    },
    messageDirection: {
      type: String,
      default: null
    },
    toolbarColor: {
      type: String,
      default: AppColor.Light
    }
  },

  data() {
    return {
      // colors
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,

      // styles
      headingClass: DashboardClass.Heading,
      labelClass: DashboardClass.Label,
      valueClass: DashboardClass.Value,

      // chart
      chartData: null,
      supportedTimePeriods: [],
      isLoading: true
    }
  },
  computed: {
    ...mapGetters(['activeResident', 'locale']),
    noChartDataForTimePeriod () {
      var total = 0
      if (!this.isNullOrUndefined(this.chartData)) {
        for (var i = 0, len = this.chartData.periodCount.length; i < len; i++) {
          total += this.chartData.periodCount[i]
        }
      }
      return total === 0
    },
    hideTimePeriodFilter () {
      if (this.isNullOrUndefined(this.supportedTimePeriods)) return true
      return this.supportedTimePeriods.length <= 1
    },
    languagedTimePeriods () {
      const self = this
      return self.supportedTimePeriods.filter(pl => {
        return pl.value === pl.value
      }).map(l => { return {
        value: l.value,
        label: this.getExpression('MenuItem', l.value),
        selected: l.selected
      }})
    },
    timePeriodLabel () {
      if (this.isNullOrUndefined(this.chartData)) return ''
      const self = this
      const tpLabels = self.languagedTimePeriods.filter(pl => {
        return pl.value === self.selectedTimePeriodValue
      })
      return tpLabels.length > 0 ? tpLabels[0].label : ''
    },
    noDataStatement () {
      if (this.isNullOrUndefined(this.chartData)) return ''

      var message = ''
      switch (this.dataSource) {
        case 'messages':
          switch (this.messageDirection) {
            case 'MessagesTo':  
              message = this.getExpression('Label', 'MessagesReceivedByResident').replace('{0}', this.activeResident.name)
              break
            case 'MessagesFrom':
              message = this.getExpression('Label', 'MessagesSentByResident').replace('{0}', this.activeResident.name)
              break
          }
          break
        case 'activation':
          message = this.getExpression('Label', 'GenieAccesses')
          break
        case 'uploads':
          message = this.getExpression('Label', 'LibraryUploads')
          break
      }

      return (
        `${this.getExpression('Alert', 'NoChartItemsFound')
          .replace('{0}', message)
          .replace('{1}', this.timePeriodLabel.toLowerCase())}.`
      )
    },
    chart () {
      if (this.isNullOrUndefined(this.chartData)) return null
      const chartData = this.chartData
      return {
        labels: chartData.labels,
        datasets: [
          {
            label: this.getExpression('Label', 'Count'),
            data: chartData.periodCount,
            backgroundColor: this.backgroundColor,
            borderWidth: 1
          }
        ]
      }
    },
    chartTitle () {
      if (this.isNullOrUndefined(this.chartData)) return null

      return `<b>${this.getExpression('Label', 'From')}</b>
        ${this.$moment(this.chartData.fromDate).format('dddd MMMM D, YYYY')}</br>
        <b>${this.getExpression('Label', 'To')}</b>
        ${this.$moment(this.chartData.toDate).format('dddd MMMM D, YYYY')}`
    },
    chartOptions () {
      if (this.isNullOrUndefined(this.chartData)) return null
      return {
        legend: { display: false },
        scales: {
          yAxes: [
            {
              ticks: {
                stepSize: this.tickStepSize
              }
            }
          ]
        }
      }
    },
    messageApiDirection () {
      if (this.isNullOrUndefined(this.messageDirection)) return null
      if (this.messageDirection === 'MessagesTo') return 'UserToResident'
      return 'ResidentToUser'
    }
  },

  methods: {
    loadData () {
      this.isLoading = true
      const self = this
      appService.getResidentDashboardBarChart(self.dataSource, self.activeResident.id, { timePeriod: self.selectedTimePeriodValue, messageDirection: self.messageApiDirection }, self.locale).then(data => {
        self.isLoading = false
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.chartData = data.chartData
          self.supportedTimePeriods = data.supportedTimePeriods
          self.$emit('data-loaded')
        }
      }).catch(e => { self.handleCriticalError(e) })
    },

    handleError (result) {
      this.$error.show({
        text: result.errorMessage,
        severity: result.errorSeverity,
        languageId: this.languageId
      })
    }
  },
  watch: {
    trigger(val) {
      if (val === null) return
      if (val) this.loadData()
    }
  }
}
</script>
